import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 * @property {HTMLElement} toggleMuteButton - The button to toggle mute
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isMuted = true;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    
    // Set initial playing state based on autoplay attribute
    this.isPlaying = this.hasAttribute('autoplay');
    this.updatePlayPauseHint(this.isPlaying);
    this.updateMuteHint(this.isMuted);
    
    // Check if media is already loaded (e.g., for autoplay videos that are rendered directly)
    if (this.querySelector('iframe, video')) {
      this.setupVideoEventListeners();
      const existingMedia = this.querySelector('iframe[data-video-type], video');
      if (existingMedia) {
        this.applyMuteState(existingMedia);
        // Ensure Vimeo videos respect loop setting
        this.applyLoopState(existingMedia);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Sets up event listeners for video end detection
   */
  setupVideoEventListeners() {
    const signal = this.#abortController.signal;
    
    // Handle HTML5 video elements
    const video = this.querySelector('video');
    if (video) {
      video.addEventListener('ended', () => {
        this.handleVideoEnded();
      }, { signal });
      
      // Listen for play event to update button when video restarts (e.g., after looping)
      video.addEventListener('play', () => {
        this.isPlaying = true;
        this.updatePlayPauseHint(this.isPlaying);
      }, { signal });
      
      // Listen for pause event to keep button state in sync
      video.addEventListener('pause', () => {
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
      }, { signal });
    }

    // Handle YouTube/Vimeo iframes via postMessage
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe instanceof HTMLIFrameElement) {
      const handleMessage = (/** @type {MessageEvent} */ event) => {
        // Security: Only accept messages from YouTube or Vimeo domains
        const allowedOrigins = [
          'https://www.youtube.com',
          'https://www.youtube-nocookie.com',
          'https://player.vimeo.com'
        ];
        
        if (!allowedOrigins.some(origin => event.origin.startsWith(origin))) {
          return;
        }

        // Verify the message is from our iframe by checking the source
        if (event.source !== iframe.contentWindow) {
          return;
        }

        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          if (iframe.dataset.videoType === 'youtube') {
            this.handleYouTubeMessage(data, event);
          } else if (iframe.dataset.videoType === 'vimeo') {
            this.handleVimeoMessage(data, event);
          }
        } catch (e) {
          // Ignore non-JSON messages or parsing errors
        }
      };

      window.addEventListener('message', handleMessage, { signal });
    }
  }

  /**
   * Handles video ended event
   */
  handleVideoEnded() {
    const video = this.querySelector('video');
    const isLooping = video?.hasAttribute('loop') || video?.loop === true;
    
    if (isLooping) {
      // If looping, video will restart automatically, so show play button (waiting state)
      // The video will continue playing, but we show play button as it's about to restart
      this.isPlaying = false;
      this.updatePlayPauseHint(this.isPlaying);
    } else {
      // If not looping, video has ended, show play button
      this.isPlaying = false;
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Handles YouTube iframe postMessage events
   * @param {any} data - The message data
   * @param {MessageEvent} event - The message event
   */
  handleYouTubeMessage(data, event) {
    // YouTube IFrame API sends state change events
    // States: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
    let state = null;
    
    // YouTube can send messages in different formats
    if (typeof data === 'number') {
      state = data;
    } else if (data && typeof data === 'object') {
      // Format: {event: 'onStateChange', info: <state>}
      if (data.event === 'onStateChange' && typeof data.info === 'number') {
        state = data.info;
      }
      // Format: {event: 'onStateChange', data: <state>}
      else if (data.event === 'onStateChange' && typeof data.data === 'number') {
        state = data.data;
      }
    }
    
    if (state === null) return;
    
    const iframe = this.querySelector('iframe[data-video-type="youtube"]');
    if (!(iframe instanceof HTMLIFrameElement)) return;
    
    // Verify the message is from this iframe's origin
    if (!event.origin.startsWith('https://www.youtube.com') && 
        !event.origin.startsWith('https://www.youtube-nocookie.com')) {
      return;
    }
    
    if (state === 0) {
      // Video ended
      const isLooping = iframe.src?.includes('loop=1');
      
      if (isLooping) {
        // If looping, video will restart, show play button (waiting state)
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
      } else {
        // Video ended, show play button
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
      }
    } else if (state === 1) {
      // Video is playing
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
    } else if (state === 2) {
      // Video is paused
      this.isPlaying = false;
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Handles Vimeo iframe postMessage events
   * @param {any} data - The message data
   * @param {MessageEvent} event - The message event
   */
  handleVimeoMessage(data, event) {
    // Vimeo Player API sends events with event name
    if (!data || typeof data !== 'object') return;
    
    const iframe = this.querySelector('iframe[data-video-type="vimeo"]');
    if (!(iframe instanceof HTMLIFrameElement)) return;
    
    // Verify the message is from Vimeo's origin
    if (!event.origin.startsWith('https://player.vimeo.com')) {
      return;
    }
    
    if (data.event === 'ended') {
      const isLooping = iframe.src?.includes('loop=1');
      
      if (isLooping) {
        // If looping, video will restart, show play button (waiting state)
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
      } else {
        // Video ended, show play button
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
      }
    } else if (data.event === 'play') {
      // Video is playing
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
    } else if (data.event === 'pause') {
      // Video is paused
      this.isPlaying = false;
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Updates the visual hint for mute state
   * @param {boolean} isMuted - Whether the video is currently muted
   */
  updateMuteHint(isMuted) {
    const toggleMuteButton = this.refs.toggleMuteButton;
    if (toggleMuteButton instanceof HTMLElement) {
      const audioOnIcon = toggleMuteButton.querySelector('.icon-audio-on');
      if (audioOnIcon) audioOnIcon.classList.toggle('hidden', isMuted);
      const audioOffIcon = toggleMuteButton.querySelector('.icon-audio-off');
      if (audioOffIcon) audioOffIcon.classList.toggle('hidden', !isMuted);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    // Pass shouldAutoPlay=true because user clicked play
    this.loadContent(true, true);
    
    // For YouTube/Vimeo iframes, wait for iframe to load before playing
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      let hasPlayed = false;
      // Wait for iframe to load before sending play command
      const playWhenReady = () => {
        if (hasPlayed) return;
        hasPlayed = true;
        // Small delay to ensure iframe API is ready
        setTimeout(() => {
          this.playMedia();
        }, 100);
      };
      
      // Check if iframe is already loaded (from autoplay scenario)
      if (iframe.src && !iframe.src.includes('about:blank')) {
        // Iframe already has a src, wait for load event or play immediately if already loaded
        iframe.addEventListener('load', playWhenReady, { once: true });
        // Also try playing immediately in case iframe is already loaded
        // @ts-ignore
        if (iframe.complete || iframe.readyState === "complete") {
          setTimeout(playWhenReady, 200);
        }
      } else {
        // New iframe, wait for load event
        iframe.addEventListener('load', playWhenReady, { once: true });
      }
    } else {
      // For regular video elements, play immediately
      this.playMedia();
    }
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   * @param {boolean} [shouldAutoPlay] - Whether to auto-play after loading (for user-initiated play)
   */
  loadContent(focus = true, shouldAutoPlay = false) {
    // Check if already loading or media already exists
    if (this.getAttribute('data-media-loading') || this.querySelector('iframe, video')) {
      return;
    }

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    // Set loading state - don't hide poster yet
    this.setAttribute('data-media-loading', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    // Wait for media to be ready before playing
    this.waitForMediaReady(content).then(() => {
      this.removeAttribute('data-media-loading');
      
      /**
       * Hides the poster after video has frame to prevent flash
       */
      const hidePoster = () => {
        // For standard controls (button poster)
        this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
        // For custom controls (div poster)
        const divPoster = this.querySelector('.deferred-media__poster');
        if (divPoster) {
          divPoster.classList.add('deferred-media__playing');
        }
      };
      
      // Only auto-play if explicitly requested (user clicked play) or video has autoplay attribute
      if (content instanceof HTMLVideoElement) {
        const hasAutoplay = content.hasAttribute('autoplay') || this.hasAttribute('autoplay');
        if (shouldAutoPlay || hasAutoplay) {
          content.play().then(() => {
            this.isPlaying = true;
            // Hide poster AFTER video starts playing (has frame) to prevent flash
            setTimeout(hidePoster, 100);
          }).catch(() => {
            // Play was prevented, still hide poster
            hidePoster();
          });
        } else {
          // Not auto-playing, hide poster after metadata loaded
          hidePoster();
        }
      } else {
        // For iframes, hide poster when ready
        hidePoster();
      }
    });

    // Apply stored mute state to the newly loaded video
    // This handles the case when user clicks unmute before video loads
    this.applyMuteState(content);

    // Ensure loop state is applied (especially for Vimeo)
    this.applyLoopState(content);

    // Initialize mute state hint
    this.updateMuteHint(this.isMuted);
    
    // Setup event listeners for video end detection
    this.setupVideoEventListeners();
  }

  /**
   * Applies the current mute state to a media element
   * @param {Node} content - The media element (video or iframe)
   */
  applyMuteState(content) {
    if (content instanceof HTMLVideoElement) {
      content.muted = this.isMuted;
    } else if (content instanceof HTMLIFrameElement) {
      // For iframes, we need to wait until they're loaded to send the message
      const applyAfterLoad = () => {
        if (this.isMuted) {
          this.muteMedia();
        } else {
          this.unmuteMedia();
        }
      };
      
      // If iframe is already loaded, apply immediately
      // @ts-ignore
      if (content.complete || content.readyState === "complete") {
        setTimeout(applyAfterLoad, 200);
      } else {
        content.addEventListener('load', () => setTimeout(applyAfterLoad, 200), { once: true });
      }
    }
  }

  /**
   * Applies the loop state to a media element
   * @param {Node} content - The media element (video or iframe)
   */
  applyLoopState(content) {
    if (content instanceof HTMLVideoElement) {
      // For native video elements, loop attribute is handled by the template
      return;
    } else if (content instanceof HTMLIFrameElement) {
      const iframe = content;
      const videoType = iframe.dataset.videoType;
      
      // Check if loop is disabled in the URL
      const isLoopDisabled = iframe.src?.includes('loop=0');
      const isLoopEnabled = iframe.src?.includes('loop=1');
      
      // Only apply if loop is explicitly disabled (loop=0) and it's a Vimeo video
      // Vimeo might loop by default even without loop=1, so we need to explicitly disable it
      if (videoType === 'vimeo' && (isLoopDisabled || (!isLoopEnabled && !iframe.src?.includes('loop=')))) {
        const disableLoop = () => {
          // Use Vimeo Player API to disable looping
          iframe.contentWindow?.postMessage(
            '{"method":"setLoop","value":false}',
            '*'
          );
        };
        
        // If iframe is already loaded, apply immediately
        // @ts-ignore
        if (iframe.complete || iframe.readyState === "complete") {
          setTimeout(disableLoop, 300);
        } else {
          iframe.addEventListener('load', () => setTimeout(disableLoop, 300), { once: true });
        }
      }
    }
  }

  /**
   * Waits for the media element to be ready to play
   * @param {Node} content - The media element (video or iframe)
   * @returns {Promise<void>}
   */
  waitForMediaReady(content) {
    return new Promise((resolve) => {
      // For native video elements
      if (content instanceof HTMLVideoElement) {
        // Check if video is already ready
        if (content.readyState >= 1) { // HAVE_METADATA or higher
          resolve();
          return;
        }
        
        // Use loadedmetadata instead of canplay for iOS Safari compatibility
        // canplay may not fire on iOS until user interaction actually plays the video
        const onLoadedMetadata = () => {
          content.removeEventListener('loadedmetadata', onLoadedMetadata);
          resolve();
        };
        content.addEventListener('loadedmetadata', onLoadedMetadata);
        
        // Short fallback timeout - native video typically loads fast
        setTimeout(() => {
          content.removeEventListener('loadedmetadata', onLoadedMetadata);
          resolve();
        }, 500);
        return;
      }
      
      // For iframe elements (YouTube/Vimeo)
      if (content instanceof HTMLIFrameElement) {
        const onLoad = () => {
          content.removeEventListener('load', onLoad);
          // Add small delay for iframe API to initialize
          setTimeout(resolve, 150);
        };
        content.addEventListener('load', onLoad);
        
        // Fallback timeout
        setTimeout(() => {
          content.removeEventListener('load', onLoad);
          resolve();
        }, 5000);
        return;
      }
      
      // For any other content, resolve immediately
      resolve();
    });
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    // If custom controls are present and media hasn't been loaded yet, load it first
    const hasMedia = this.querySelector('iframe, video');
    if (this.refs.toggleMediaButton && !hasMedia && !this.getAttribute('data-media-loading')) {
      this.showDeferredMedia();
      return;
    }

    // If still loading, don't allow toggle yet (wait for load to complete)
    if (this.getAttribute('data-media-loading')) {
      return;
    }

    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // Update play/pause hint if media is loaded
    if (this.querySelector('iframe, video')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Toggles mute state of the media
   */
  toggleMute() {
    if (this.isMuted) {
      this.unmuteMedia();
    } else {
      this.muteMedia();
    }
  }

  /**
   * Mutes the media
   */
  muteMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"mute","args":""}'
          : '{"method":"setMuted","value":true}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = true;
      }
    }
    this.isMuted = true;
    this.updateMuteHint(this.isMuted);
  }

  /**
   * Unmutes the media
   */
  unmuteMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"unMute","args":""}'
          : '{"method":"setMuted","value":false}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = false;
      }
    }
    this.isMuted = false;
    this.updateMuteHint(this.isMuted);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
